import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Store } from "lucide-react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SELLER_PROFILE_STATUS_LABELS } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { toast } from "../stores/toast";
import { Badge, Button, Input, statusColor } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { flattenInfinitePages, getNextPageParam } from "../lib/infinite-list";
import { fmtDate, num } from "../lib/format";
import { showQueryLoader } from "../lib/query-utils";

interface SellerRow {
  id: string;
  shopName: string;
  shopSlug: string;
  status: string;
  platformFeePercent: number | null;
  completedSales: number;
  avgRating: number;
  ratingCount: number;
  activeListings: number;
  impressions7d: number;
  updatedAt: string;
  user: { email: string; displayName: string | null };
}

const PAGE_SIZE = 25;

export function MarketplaceSellers() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [feeInput, setFeeInput] = useState("");

  const listQuery = useInfiniteQuery({
    queryKey: ["marketplace-sellers", search],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ page: String(pageParam), pageSize: String(PAGE_SIZE) });
      if (search.trim()) params.set("q", search.trim());
      return api<{ items: SellerRow[]; total: number }>(`/marketplace/admin/sellers?${params}`);
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(lastPage.total, allPages.reduce((acc, p) => acc + p.items.length, 0), PAGE_SIZE),
  });

  const detailQuery = useQuery({
    queryKey: ["marketplace-seller", selectedId],
    queryFn: () =>
      api<{
        seller: SellerRow & { user: { phone?: string } };
      }>(`/marketplace/admin/sellers/${selectedId}`),
    enabled: !!selectedId,
  });

  const patchMut = useMutation({
    mutationFn: (body: { platformFeePercent?: number | null; status?: string }) =>
      api(`/marketplace/admin/sellers/${selectedId}`, { method: "PATCH", body }),
    onSuccess: () => {
      toast("success", "Seller updated");
      void qc.invalidateQueries({ queryKey: ["marketplace-sellers"] });
      void qc.invalidateQueries({ queryKey: ["marketplace-seller", selectedId] });
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Update failed"),
  });

  const columns = useMemo<Column<SellerRow>[]>(
    () => [
      {
        key: "shop",
        header: "Shop",
        sortable: true,
        sortValue: (r) => r.shopName,
        render: (r) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{r.shopName}</p>
            <p className="font-mono text-[10px] text-muted">{r.shopSlug}</p>
          </div>
        ),
      },
      {
        key: "seller",
        header: "Account",
        hideSm: true,
        render: (r) => (
          <div className="min-w-0">
            <p className="truncate text-xs text-foreground">{r.user.displayName ?? r.user.email}</p>
            <p className="truncate text-[10px] text-muted">{r.user.email}</p>
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        sortValue: (r) => r.status,
        render: (r) => (
          <Badge color={statusColor(r.status === "ACTIVE" ? "ACTIVE" : "PENDING")}>
            {SELLER_PROFILE_STATUS_LABELS[r.status] ?? r.status}
          </Badge>
        ),
      },
      {
        key: "listings",
        header: "Active listings",
        className: "text-right",
        sortValue: (r) => r.activeListings,
        render: (r) => <span className="text-xs tabular-nums">{num(r.activeListings)}</span>,
      },
      {
        key: "impressions",
        header: "Impressions (7d)",
        className: "text-right",
        hideSm: true,
        sortValue: (r) => r.impressions7d,
        render: (r) => <span className="text-xs tabular-nums">{num(r.impressions7d)}</span>,
      },
      {
        key: "sales",
        header: "Sales",
        className: "text-right",
        sortValue: (r) => r.completedSales,
        render: (r) => <span className="text-xs tabular-nums">{num(r.completedSales)}</span>,
      },
      {
        key: "rating",
        header: "Rating",
        className: "text-right",
        sortValue: (r) => r.avgRating,
        render: (r) => (
          <span className="text-xs tabular-nums">
            {r.ratingCount > 0 ? `${Number(r.avgRating).toFixed(1)} (${r.ratingCount})` : "—"}
          </span>
        ),
      },
      {
        key: "fee",
        header: "Platform fee",
        className: "text-right",
        hideSm: true,
        render: (r) => (
          <span className="text-xs text-muted">{r.platformFeePercent != null ? `${r.platformFeePercent}%` : "Default"}</span>
        ),
      },
      {
        key: "updated",
        header: "Updated",
        hideSm: true,
        sortValue: (r) => r.updatedAt,
        render: (r) => <span className="text-xs text-muted">{fmtDate(r.updatedAt)}</span>,
      },
    ],
    [],
  );

  const rows = flattenInfinitePages(listQuery.data?.pages);
  const listTotal = listQuery.data?.pages[0]?.total ?? 0;
  const hasNextPage = rows.length < listTotal && (listQuery.hasNextPage ?? false);
  const seller = detailQuery.data?.seller;

  return (
    <ListPageShell>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={showQueryLoader(listQuery)}
        entityLabel="Seller"
        exportFilename="marketplace-sellers"
        infiniteScroll={{
          hasNextPage,
          isFetchingNextPage: listQuery.isFetchingNextPage,
          onLoadMore: () => void listQuery.fetchNextPage(),
        }}
        onRowClick={(r) => {
          setSelectedId(r.id);
          setFeeInput(r.platformFeePercent != null ? String(r.platformFeePercent) : "");
        }}
        activeKey={selectedId ?? undefined}
        toolbar={{
          search: {
            value: search,
            onChange: setSearch,
            onSubmit: () => void listQuery.refetch(),
            placeholder: "Search shop or email",
          },
          onRefresh: () => void listQuery.refetch(),
        }}
        empty={{ icon: Store, title: "No sellers", hint: "Sellers appear after they apply in the seller portal." }}
      />

      <RightSidebarPanel
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        title={seller?.shopName ?? "Seller"}
        footer={
          seller && seller.status !== "KYC_SUBMITTED" && seller.status !== "KYC_REJECTED" ? (
            <div className="flex w-full flex-wrap justify-end gap-2">
              <Button
                variant="primary"
                size="sm"
                disabled={patchMut.isPending}
                onClick={() =>
                  patchMut.mutate({
                    platformFeePercent: feeInput.trim() === "" ? null : Number(feeInput),
                  })
                }
              >
                Save
              </Button>
              {seller.status === "ACTIVE" ? (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={patchMut.isPending}
                  onClick={() => void patchMut.mutate({ status: "SUSPENDED" })}
                >
                  Suspend
                </Button>
              ) : null}
            </div>
          ) : null
        }
      >
        {seller && (
          <div className="space-y-6 text-sm">
            <section className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">Account</p>
              <p className="text-foreground">{seller.user.email}</p>
              {seller.user.displayName ? <p className="text-muted">{seller.user.displayName}</p> : null}
              <Badge color={statusColor(seller.status === "ACTIVE" ? "active" : "pending")}>
                {SELLER_PROFILE_STATUS_LABELS[seller.status] ?? seller.status}
              </Badge>
            </section>

            {(seller.status === "KYC_SUBMITTED" || seller.status === "KYC_REJECTED") && (
              <section className="rounded-xl border border-line bg-surface-2/50 p-4">
                <p className="text-sm text-foreground">KYC review happens in the Verification hub.</p>
                <Link
                  to="/commerce/verifications?tab=seller"
                  className="mt-2 inline-block text-sm font-medium text-brand-blue hover:underline"
                >
                  Review in Verification
                </Link>
              </section>
            )}

            {seller.status !== "KYC_SUBMITTED" && seller.status !== "KYC_REJECTED" && (
              <section className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">Platform fee</p>
                <Input
                  label="Platform fee %"
                  value={feeInput}
                  onChange={(e) => setFeeInput(e.target.value)}
                  hint="Leave blank for global default"
                />
              </section>
            )}
          </div>
        )}
      </RightSidebarPanel>
    </ListPageShell>
  );
}
